import { Form, Formik, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import { SelectField } from "@/features/settings/components/select-field";
import {
  createTaxForm,
  type TaxForm,
  type TaxFormCategory,
  TAX_FORM_CATEGORY_OPTIONS,
  updateTaxForm,
} from "@/features/reference/api";
import { applyApiErrors } from "@/lib/form-errors";

const schema = Yup.object({
  formNumber: Yup.string().trim().max(20, "Max 20 characters").required("Form number is required"),
  title: Yup.string().trim().max(120, "Max 120 characters").required("Title is required"),
  category: Yup.string().required(),
  appliesToIndividuals: Yup.boolean(),
  appliesToBusinesses: Yup.boolean(),
  isActive: Yup.boolean(),
  fileName: Yup.string().max(120),
});

interface FormValues {
  formNumber: string;
  title: string;
  category: TaxFormCategory;
  appliesToIndividuals: boolean;
  appliesToBusinesses: boolean;
  isActive: boolean;
  fileName: string;
}

interface Props {
  taxForm?: TaxForm;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function TaxFormDialog({ taxForm, open, onOpenChange, onSaved }: Props) {
  const isEdit = !!taxForm;

  async function handleSubmit(values: FormValues, { setErrors }: FormikHelpers<FormValues>) {
    try {
      if (isEdit) await updateTaxForm(taxForm.id, values);
      else await createTaxForm(values);
      onSaved();
      onOpenChange(false);
      toast.success(isEdit ? "Tax form updated" : "Tax form added");
    } catch (err) {
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save the tax form." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit tax form" : "Add tax form"}</DialogTitle>
          <DialogDescription>
            The registry the form engine keys per-form persistence and the picker off.
          </DialogDescription>
        </DialogHeader>
        <Formik<FormValues>
          initialValues={{
            formNumber: taxForm?.formNumber ?? "",
            title: taxForm?.title ?? "",
            category: taxForm?.category ?? "OTHER",
            appliesToIndividuals: taxForm?.appliesToIndividuals ?? true,
            appliesToBusinesses: taxForm?.appliesToBusinesses ?? true,
            isActive: taxForm?.isActive ?? true,
            fileName: taxForm?.fileName ?? "",
          }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <FormField name="formNumber" label="Form number" placeholder="2848" />
                <SelectField name="category" label="Category" options={TAX_FORM_CATEGORY_OPTIONS} />
              </div>
              <FormField name="title" label="Title" />
              <FormField name="fileName" label="PDF file name" placeholder="f2848.pdf" />
              {isEdit && taxForm.irsTitle && (
                <p className="text-muted-foreground text-xs">
                  IRS PDF title (drift source): <span className="font-medium">{taxForm.irsTitle}</span>
                </p>
              )}
              <div className="flex flex-wrap gap-6">
                <CheckboxField name="appliesToIndividuals" label="Individuals" />
                <CheckboxField name="appliesToBusinesses" label="Businesses" />
                <CheckboxField name="isActive" label="Active" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : isEdit ? "Save changes" : "Add form"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
